import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Clock3, Sparkles, Wifi, BatteryFull, Check, RotateCcw, Loader2 } from "lucide-react";
import { MaskedLines, Reveal } from "./ui";

const EASE = [0.16, 1, 0.3, 1];

const SITUATIONS = [
  {
    id: "agenda",
    label: "Agenda fraca",
    module: "Agenda Viva",
    mission: "Chame 3 clientes que deveriam ter retornado esta semana.",
    why: "Sua agenda está fraca e você ainda não tentou reativação.",
    time: "±15 min",
    done: "Termina quando as 3 mensagens forem enviadas",
  },
  {
    id: "tempo",
    label: "Tempo livre",
    module: "Tempo Livre",
    mission: "Poste 1 antes-e-depois nos stories marcando a cliente.",
    why: "Janelas curtas rendem prova social sem travar sua operação.",
    time: "±10 min",
    done: "Termina quando o story for publicado",
  },
  {
    id: "caixa",
    label: "Caixa hoje",
    module: "Caixa Hoje",
    mission: "Ofereça 1 tratamento pré-pago para 2 clientes fieis.",
    why: "Venda futura traz caixa para hoje sem mexer no seu preço.",
    time: "±20 min",
    done: "Termina quando as 2 ofertas forem enviadas",
  },
  {
    id: "clientes",
    label: "Clientes sumidas",
    module: "Agenda Viva",
    mission: "Envie um “senti sua falta” para 5 clientes que sumiram há 60+ dias.",
    why: "Reativar custa menos do que conquistar uma cliente nova.",
    time: "±15 min",
    done: "Termina quando as 5 mensagens forem enviadas",
  },
  {
    id: "recepcao",
    label: "Sem recepção",
    module: "Basic Recepção",
    mission: "Confirme por WhatsApp os 3 próximos horários da agenda.",
    why: "Confirmação é o mínimo que não pode parar sem recepcionista.",
    time: "±10 min",
    done: "Termina quando os 3 horários forem confirmados",
  },
];

export function PhoneDemo() {
  const [selected, setSelected] = useState(null);
  const [phase, setPhase] = useState("pick");
  const situation = SITUATIONS.find((s) => s.id === selected);

  const generate = () => {
    if (!selected) return;
    setPhase("loading");
    setTimeout(() => setPhase("mission"), 1100);
  };

  const reset = () => {
    setPhase("pick");
    setSelected(null);
  };

  return (
    <div className="w-[280px] rounded-[2.6rem] border border-white/15 bg-hc-deep p-2 shadow-[0_30px_90px_rgba(0,0,0,0.7)] sm:w-[300px]">
      <div className="overflow-hidden rounded-[2.1rem] bg-gradient-to-b from-mom-vinho to-mom-vinhoDark">
        <div className="flex items-center justify-between px-5 pt-3 text-[9px] text-white/70">
          <span>9:41</span>
          <span className="flex items-center gap-1.5">
            <Wifi className="h-3 w-3" />
            <BatteryFull className="h-3 w-3" />
          </span>
        </div>
        <div className="flex h-[440px] flex-col px-5 pb-5 pt-3">
          <div className="flex items-center justify-between">
            <p className="font-dmsans text-sm font-bold tracking-[0.12em] text-white">MOM</p>
            <span className="flex items-center gap-1.5 text-[10px] text-hc-green">
              <span className="h-1.5 w-1.5 rounded-full bg-hc-green" />
              agora
            </span>
          </div>

          <AnimatePresence mode="wait">
            {phase === "pick" && (
              <motion.div
                key="pick"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35, ease: EASE }}
                className="flex flex-1 flex-col"
              >
                <p className="mt-5 font-dmsans text-[15px] font-medium leading-snug text-white">
                  O que está acontecendo no salão agora?
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {SITUATIONS.map((s) => (
                    <button
                      key={s.id}
                      data-testid={`demo-situation-${s.id}`}
                      onClick={() => setSelected(s.id)}
                      className={`rounded-full border px-3.5 py-2 font-dmsans text-[11px] font-medium transition-colors duration-300 ${
                        selected === s.id
                          ? "border-transparent bg-white text-mom-vinho"
                          : "border-white/15 bg-black/20 text-white/75 hover:border-white/30 hover:text-white"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <div className="mt-auto">
                  <p className="mb-3 text-center font-dmsans text-[10px] text-white/40">
                    Toque em uma situação — é assim de verdade
                  </p>
                  <button
                    data-testid="demo-generate"
                    onClick={generate}
                    disabled={!selected}
                    className={`w-full rounded-full py-3 font-dmsans text-[11px] font-semibold transition-all duration-300 ${
                      selected
                        ? "bg-white text-mom-vinho hover:bg-white/90"
                        : "cursor-not-allowed bg-black/25 text-white/30"
                    }`}
                  >
                    Gerar minha missão
                  </button>
                </div>
              </motion.div>
            )}

            {phase === "loading" && (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex flex-1 flex-col items-center justify-center gap-4"
              >
                <Loader2 className="h-7 w-7 animate-spin text-white" />
                <p className="font-dmsans text-[11px] text-white/60">
                  Cruzando situação, tempo e prioridade…
                </p>
              </motion.div>
            )}

            {(phase === "mission" || phase === "done") && situation && (
              <motion.div
                key="mission"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="flex flex-1 flex-col"
              >
                <div
                  data-testid="demo-mission-card"
                  className="mt-5 rounded-2xl border border-white/15 bg-black/35 p-4"
                >
                  <div className="flex items-center justify-between">
                    <p className="font-dmsans text-[9px] font-semibold uppercase tracking-[0.22em] text-white/80">
                      Missão · {situation.module}
                    </p>
                    <span className="flex items-center gap-1 font-dmsans text-[9px] font-semibold text-white/50">
                      <Clock3 className="h-2.5 w-2.5" /> {situation.time}
                    </span>
                  </div>
                  <p className="mt-2.5 font-dmsans text-[15px] font-medium leading-snug text-white">
                    {situation.mission}
                  </p>
                  <p className="mt-2.5 flex items-start gap-1.5 font-dmsans text-[10px] leading-relaxed text-white/60">
                    <Sparkles className="mt-0.5 h-2.5 w-2.5 shrink-0 text-white" />
                    {situation.why}
                  </p>
                  <div className="mt-3.5 border-t border-dashed border-white/15 pt-2.5 font-dmsans text-[9px] uppercase tracking-[0.14em] text-white/45">
                    {situation.done}
                  </div>
                </div>

                <div className="mt-auto space-y-2.5">
                  {phase === "mission" ? (
                    <button
                      data-testid="demo-complete"
                      onClick={() => setPhase("done")}
                      className="w-full rounded-full bg-white py-3 font-dmsans text-[11px] font-semibold text-mom-vinho transition-colors hover:bg-white/90"
                    >
                      Concluir missão
                    </button>
                  ) : (
                    <motion.div
                      data-testid="demo-done"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      className="rounded-2xl border border-hc-green/40 bg-black/35 p-3.5 text-center"
                    >
                      <p className="flex items-center justify-center gap-1.5 font-dmsans text-[11px] font-semibold text-hc-green">
                        <Check className="h-3.5 w-3.5" /> Missão concluída
                      </p>
                      <p className="mt-1 font-dmsans text-[10px] text-white/55">
                        Imagina isso todo dia, toda hora vazia.
                      </p>
                    </motion.div>
                  )}
                  <button
                    data-testid="demo-reset"
                    onClick={reset}
                    className="flex w-full items-center justify-center gap-1.5 py-1 font-dmsans text-[10px] font-medium text-white/45 transition-colors hover:text-white"
                  >
                    <RotateCcw className="h-3 w-3" /> Testar outra situação
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default function ProblemBlock() {
  return (
    <section data-testid="mom-problema" className="bg-black text-white">
      <div className="grid grid-cols-1 lg:grid-cols-12">
        <div className="relative min-h-[280px] overflow-hidden lg:col-span-3 lg:min-h-[560px]">
          <img
            loading="lazy"
            src="/mom/mecanismo-d.jpg"
            alt="Cliente relaxando no lavatório"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
        <div className="flex flex-col justify-center px-6 py-16 sm:px-12 lg:col-span-5 lg:py-24">
          <MaskedLines
            inView
            as="h2"
            className="font-serif text-5xl font-medium leading-[1.05] tracking-tight sm:text-6xl"
            lines={[
              "O problema",
              "entra.",
              <em key="m" className="italic text-mom-carmim">Uma missão sai.</em>,
            ]}
          />
          <Reveal delay={0.2}>
            <div className="mt-8 max-w-md space-y-2 font-dmsans text-sm leading-relaxed text-white/65 sm:text-base">
              <p>Você conta o que está acontecendo.</p>
              <p>O MOM entende o contexto.</p>
              <p className="text-white">Você recebe uma missão para executar agora.</p>
            </div>
            <p className="mt-8 font-serif text-xl font-medium italic text-mom-carmim sm:text-2xl">
              Sem planner. Sem vinte opções para escolher.
            </p>
            <span className="mt-8 block h-px w-10 bg-mom-carmim" />
            <p className="mt-6 font-dmsans text-[10px] font-medium uppercase tracking-[0.28em] text-white/50">
              Inteligência real<br />para profissionais reais.
            </p>
          </Reveal>
        </div>
        <div className="flex items-center justify-center gap-10 px-6 pb-16 lg:col-span-4 lg:pb-0">
          <Reveal delay={0.3}>
            <PhoneDemo />
          </Reveal>
          <p className="hidden font-dmsans text-[10px] font-medium uppercase leading-loose tracking-[0.28em] text-white/45 xl:block">
            Ideias<br />Soluções<br />Rotina<br /><span className="text-white/80">Resultados</span>
          </p>
        </div>
      </div>
    </section>
  );
}
